/**
 * aggregation.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Modal } from "angular2-modal/plugins/bootstrap";

import { Aggregation } from "./aggregation";
import { AggregationService } from "./aggregation.service";
import { Aggregator } from "./aggregator";
import { ToolSchema } from "./schemas";

@Component({
  template: `
<div class="container" #agg>
    <div [class.col-sm-4]="!agg.hideTools" [class.col-sm-2]="agg.hideTools">
				<h2 (click)="agg.hideTools = !agg.hideTools">
						Toolbox
						<span
								class="glyphicon"
								[class.glyphicon-collapse-down]="!agg.hideTools"
								[class.glyphicon-collapse-up]="agg.hideTools">
						</span>
				</h2>
				<div class="well">
						<em *ngIf="agg.hideTools">(Hidden)</em>
            <aggregator-list class="collapse" [class.in]="!agg.hideTools"></aggregator-list>
        </div>
    </div>

    <!-- OPTIONS & RESULTS -->
    <div [class.col-sm-8]="!agg.hideTools" [class.col-sm-10]="agg.hideTools">
        <ul class="nav nav-pills" role="tablist">
            <li role="presentation" [class.active]="service.selectedPanel == 0">
                <a (click)="service.selectedPanel = 0">
                    <!--<span class="glyphicon glyphicon-signal"></span>-->
                    Analysis Tasks
                    <span class="badge">
                        {{service.aggregations.length}}
                    </span>
                </a>
            </li>
            <li role="presentation" [class.active]="service.selectedPanel == 1">
                <a (click)="service.selectedPanel = 1">
                    <!--<span class="glyphicon glyphicon-export"></span>-->
                    Data Export Tasks
                    <span class="badge">
                        {{service.exports.length}}
                    </span>
                </a>
            </li>
            <li role="presentation" [class.active]="service.selectedPanel == 2">
                <a (click)="service.selectedPanel = 2">
                    Saved Templates
                    <span class="badge">
                        {{service.schemaCount}}
                    </span>
                </a>
            </li>
        </ul>

        <div *ngIf="service.selectedPanel === 0">
            <h3 *ngIf="!service.aggregations.length">No current queries!</h3>
            <aggregation-task
                *ngFor="let task of service.aggregations"
                [task]="task">
            </aggregation-task>
        </div>
        <div *ngIf="service.selectedPanel === 1">
            <h3 *ngIf="!service.exports.length">No current queries!</h3>
            <export-task
                *ngFor="let task of service.exports"
                [task]="task">
            </export-task>
        </div>
        <div *ngIf="service.selectedPanel === 2">
            <p>
                You can create a template for a tool by running that tool with
                your desired settings, and then selecting
                <kbd>Save as Template</kbd>
                from the tool's action bar.
            </p>

            <h3>
                Analysis Templates
                <span class="badge">{{service.aggregationSchemas.length}}</span>
            </h3>
            <table class="table p100">
            <thead>
                <th
                    [sorts]="service.aggregationSchemas"
                    dot="schemaName"
                    asType="string"
                    [autorun]="true">
                    Name
                </th>
                <th
                    [sorts]="service.aggregationSchemas"
                    dot="aggregatorName"
                    asType="string">
                    Type
                </th>
                <th
                    [sorts]="service.aggregationSchemas"
                    dot="algorithm"
                    asType="string">
                    Algorithm
                </th>
                <th
                    [sorts]="service.aggregationSchemas"
                    dot="groupId"
                    asType="integer">
                    Group
                </th>
                <th
                    [sorts]="service.aggregationSchemas"
                    dot="isGroupPublic"
                    asType="boolean">
                    Visibility
                </th>
                <th></th>
            </thead>
            <tbody>
            <tr *ngFor="let schema of service.aggregationSchemas">
                <th>{{schema.schemaName}}</th>
                <td>{{schema.aggregatorName}}</td>
                <td>{{schema.algorithmObject?.displayName}}</td>
                <td>{{schema.group?.name}}</td>
                <td (click)="changeVisibility(schema, false)">
                    <span [class.dotted]="schema.isOwner">
                        <span
                            class="glyphicon"
                            [class.glyphicon-eye-open]="schema.isGroupPublic"
                            [class.glyphicon-eye-close]="!schema.isGroupPublic">
                        </span>
                        {{schema.isGroupPublic ? "group" : "only me"}}
                    </span>
                </td>
                <td class="btn-group">
                    <button
                        class="btn btn-xs btn-primary"
                        (click)="startFromSchema(schema, false)">
                        <span class="glyphicon glyphicon-wrench"></span>
                        Open
                    </button>
                    <button
                        class="btn btn-xs btn-danger"
                        (click)="deleteSchema(schema, false)"
                        [disabled]="!schema.isOwner">
                        <span class="glyphicon glyphicon-remove"></span>
                        Delete
                    </button>
                </td>
            </tr>
            <tr *ngIf="!service.aggregationSchemas.length">
                <td class="text-center" colspan="6">
                    <h4>No templates found</h4>
                </td>
            </tr>
            </tbody>
        </table>
            <!-- <prettyjson [obj]="schema"></prettyjson> -->

            <h3>
                Data Export Templates
                <span class="badge">{{service.exportSchemas.length}}</span>
            </h3>
            <table class="table p100">
                <thead>
                    <th>
                        <span
                            [sorts]="service.exportSchemas"
                            storedAs="exportTemplateSorting"
                            dot="schemaName"
                            asType="string"
                            [autorun]="true">
                            Name
                        </span>
                        /
                        <span
                            [sorts]="service.exportSchemas"
                            storedAs="exportTemplateSorting"
                            dot="format.displayName"
                            asType="string">
                            Type
                        </span>
                    </th>
                    <th>
                        <span
                            [sorts]="service.exportSchemas"
                            storedAs="exportTemplateSorting"
                            dot="creator"
                            asType="string">
                            Creator
                        </span>
                        /
                        <span
                            [sorts]="service.exportSchemas"
                            storedAs="exportTemplateSorting"
                            dot="algorithm"
                            asType="string">
                            Algorithm
                        </span>
                    </th>
                    <th>
                        <span
                            [sorts]="service.exportSchemas"
                            storedAs="exportTemplateSorting"
                            dot="groupId"
                            asType="integer">
                            Group
                        </span>
                        /
                        <span
                            [sorts]="service.exportSchemas"
                            storedAs="exportTemplateSorting"
                            dot="isGroupPublic"
                            asType="boolean">
                            Visibility
                        </span>
                    </th>
                    <th></th>
                </thead>
                <tbody>
                    <tr *ngFor="let schema of service.exportSchemas">
                        <td>
                            <strong>{{schema.schemaName}}</strong>
                            <br>
                            {{schema.format.displayName}}
                        </td>
                        <td>
                            {{schema.creator}}
                            <br>
                            {{schema.algorithmObject?.displayName}}
                        </td>
                        <td>
                            {{schema.group?.name}}
                            <br>
                            <div (click)="changeVisibility(schema, true)">
                                <span [class.dotted]="schema.isOwner">
                                    <span
                                        class="glyphicon"
                                        [class.glyphicon-eye-open]="schema.isGroupPublic"
                                        [class.glyphicon-eye-close]="!schema.isGroupPublic">
                                    </span>
                                    {{schema.isGroupPublic ? "group" : "only me"}}
                                </span>
                            </div>
                        </td>
                        <td class="btn-group">
                            <button
                                class="btn btn-primary"
                                (click)="startFromSchema(schema, true, false)">
                                <span class="glyphicon glyphicon-wrench"></span>
                                Open
                            </button>
                            <button
                                class="btn btn-danger"
                                (click)="deleteSchema(schema, true)"
                                [disabled]="!schema.isOwner">
                                <span class="glyphicon glyphicon-remove"></span>
                                Delete
                            </button>
                        </td>
                    </tr>
                </tbody>
                <tr *ngIf="!service.exportSchemas.length">
                    <td class="text-center" colspan="6">
                        <h4>No templates found</h4>
                    </td>
                </tr>
            </table>
        </div>
    </div>
</div>
  `,
  styles: [
      `.nav-pills {
          padding-top: 0.75em;
          padding-bottom: 0.7em;
      }`
  ]
})
export class AggregationComponent
{
    constructor (
        public service : AggregationService,
        public modal : Modal
    ) { /* DI */ }

    public startFromSchema (
        schema : ToolSchema,
        isExport : boolean
    ) : void
    {
        this.service.startFromSchema(schema, isExport);
    }

    public deleteSchema (schema : ToolSchema, isExport : boolean) : void
    {
        this.service.deleteSchema(schema, isExport);
    }

    public changeVisibility (schema : ToolSchema, isExport : boolean) : void
    {
        if (!schema.isOwner)
        {
            return;
        }
        this.service.toggleSchemaVisibility(schema, isExport);
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/aggregation/aggregation.component.ts